<script setup>
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, Trash2 } from 'lucide-vue-next'
import { ref } from 'vue'
import { MapSource, MapLayer } from '@/components/map'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  map: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['select', 'delete'])

const selectFeature = () => {
  emit('select', props.feature)
}

const deleteFeature = () => {
  emit('delete', props.feature)
}

const showPreview = ref(false)

const togglePreview = () => {
  showPreview.value = !showPreview.value
}
</script>

<template>
  <div
    class="border-gray-200 border-b last:border-b-0 cursor-pointer"
    :class="{'bg-secondary': isSelected}"
  >
    <div class="flex justify-between items-center">
      <div @click="selectFeature" class="w-full px-2 py-1">
        <div class="flex items-center justify-between">
          <h4 class="text-sm text-gray-600 mb-0.5" :class="{'font-semibold text-primary': isSelected}">
            <span v-if="!feature.properties || !feature.properties.name">Drawed ({{ index + 1 }})</span>
            <span v-else>
              {{ feature.properties.name }}
            </span>
          </h4>
        </div>
        <ul class="text-gray-400 text-xs">
          <li v-if="feature.properties && feature.properties.addresstype" class="capitalize">Type: {{ feature.properties.addresstype }}</li>
          <li v-if="feature.geometry && feature.geometry.type">Geometry: {{ feature.geometry.type }}</li>
          <li v-else>Geometry: Unknown</li>
          <li v-if="feature.properties && feature.properties.areaText">Size: {{ feature.properties.areaText }}</li>
        </ul>
      </div>
      <div class="flex mr-2 gap-2">
        <Button
          variant="ghost"
          size="icon"
          @click="togglePreview"
          :class="{'text-primary': showPreview}"
        >
          <EyeOff v-if="!showPreview" class="h-4 w-4" />
          <Eye v-else class="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          @click="deleteFeature"
          class="text-red-500 hover:text-red-700"
        >
          <Trash2 class="h-4 w-4" />
        </Button>
        <Button
          v-if="!isSelected"
          variant="secondary"
          size="sm"
          @click="selectFeature"
        >Select</Button>
      </div>
    </div>

    <!-- Map Preview -->
    <MapSource
      v-if="showPreview && feature"
      :map="map"
      type="geojson"
      :id="`preview-feature-${index}`"
      :data="{
        type: 'FeatureCollection',
        features: [feature]
      }"
      :options="{
        'generateId': true
      }"
    >
      <MapLayer
        :map="map"
        :id="`preview-feature-fill-${index}`"
        type="fill"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'fill-color': '#059669',
            'fill-opacity': 0.1
          },
          'filter': ['==', '$type', 'Polygon']
        }"
      ></MapLayer>

      <MapLayer
        :map="map"
        :id="`preview-feature-line-${index}`"
        type="line"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'line-color': '#059669',
            'line-width': 2
          }
        }"
      ></MapLayer>
    </MapSource>
  </div>
</template>
